import { Form } from 'antd';
import { useContext, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import {
  useLazyGetLegalEntityByIdQuery,
  useUpdateLegalEntityMutation,
} from '@app/api/LegalEntities';
import {
  RIGHTS_IS_CREATE,
  RIGHTS_IS_EDIT,
  RIGHTS_IS_ON,
  RIGHTS_LEGAL_ENTITIES,
} from '@app/constants/accessRights';
import { ERROR, SUCCESS, WARNING } from '@app/constants/alerts';
import { LEGAL_ENTITIES_PATH } from '@app/constants/navigation';
import { useAccessRights, useUserPrint } from '@app/hooks';
import { LeavePageContext, NotificationContext } from '@app/providers';
import type { NNotificationProvider } from '@app/providers/NotificationProvider/NotificationProvider.types';
import type { NLegalEntities } from '@app/types/LegalEntities';

export const useAddLegalEntities = () => {
  const { activeBranchId } = useUserPrint();
  const [getLegalEntityById] = useLazyGetLegalEntityByIdQuery();
  const [updateLegalEntity] = useUpdateLegalEntityMutation();
  const { hasUserRightForModuleView } = useAccessRights(
    RIGHTS_LEGAL_ENTITIES,
    RIGHTS_IS_ON
  );
  const { hasUserRight: hasUserRightToCreate } = useAccessRights(
    RIGHTS_LEGAL_ENTITIES,
    RIGHTS_IS_CREATE
  );
  const { hasUserRight: hasUserRightToEdit } = useAccessRights(
    RIGHTS_LEGAL_ENTITIES,
    RIGHTS_IS_EDIT
  );
  const [isLoading, setIsLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [form] = Form.useForm();
  const [serverError, setServerError] = useState('');
  const [legalEntity, setLegalEntity] =
    useState<NLegalEntities.ModelLegalEntity>();
  const [searchParams] = useSearchParams();
  const editableLegalEntitiesId = Number(searchParams.get('id'));
  const { showNotification } = useContext(NotificationContext);
  const navigate = useNavigate();
  const isAddingOrEditingPage = window.location.pathname.includes(
    `${LEGAL_ENTITIES_PATH}/add`
  );

  const { setNewData, prepareStartedData, checkIsDataUpdated, setInputData } =
    useContext(LeavePageContext);

  useEffect(() => {
    const fetchLegalEntity = async () => {
      if (editableLegalEntitiesId) {
        setIsLoading(true);

        try {
          const result = await getLegalEntityById({
            ActiveBranchId: activeBranchId,
            legalEntityId: editableLegalEntitiesId,
          }).unwrap();

          form.setFieldsValue({
            Name: result.Name,
            TaxPayerId: result.TaxPayerId,
            Description: result.Description,
            Kpp: result.Kpp,
            isDeleted: result.IsDeleted,
          });

          setLegalEntity(result);
          prepareStartedData(result);
        } catch (error) {
          const message = error instanceof Error ? error.message : '';
          const cause = error instanceof Error ? (error.cause as string) : '';

          showNotification(message, cause as NNotificationProvider.NotifyType);
        } finally {
          setIsLoading(false);
        }
      } else {
        prepareStartedData({
          Name: undefined,
          TaxPayerId: undefined,
          Kpp: undefined,
          Description: undefined,
        });
      }
    };

    fetchLegalEntity();
  }, [
    activeBranchId,
    getLegalEntityById,
    editableLegalEntitiesId,
    form,
    showNotification,
    prepareStartedData,
  ]);

  const submitForm = async (values: NLegalEntities.AddLegalEntityFormProps) => {
    try {
      setIsLoading(true);

      const isCreateMode = !legalEntity;

      await updateLegalEntity({
        ActiveBranchId: activeBranchId,
        data: {
          Id: isCreateMode ? 0 : legalEntity.Id,
          Name: values.Name || null,
          Description: values.Description ? values.Description.trim() : null,
          IsDeleted: isCreateMode ? false : legalEntity.IsDeleted,
          Kpp: values.Kpp ? +values.Kpp : null,
          TaxPayerId: values.TaxPayerId ? +values.TaxPayerId : null,
          CountConstantsPayments: isCreateMode
            ? 0
            : legalEntity.CountConstantsPayments,
        },
      }).unwrap();

      const successMessage = isCreateMode
        ? 'Юридическое лицо создано успешно'
        : 'Юридическое лицо обновлено успешно';

      showNotification(successMessage, SUCCESS);
      navigate(LEGAL_ENTITIES_PATH);
    } catch (error) {
      const message = error instanceof Error ? error.message : '';
      const cause = error instanceof Error ? (error.cause as string) : '';

      if (cause === ERROR) {
        showNotification(message, cause);
      } else if (cause === WARNING) {
        setServerError(message);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    checkIsDataUpdated({
      path: LEGAL_ENTITIES_PATH,
      navigate,
    });
  };

  const handelDeleteLegalEntity = async () => {
    try {
      setIsLoading(true);

      await updateLegalEntity({
        ActiveBranchId: activeBranchId,
        data: {
          Id: legalEntity?.Id || 0,
          Name: legalEntity?.Name || null,
          Description: legalEntity?.Description || null,
          IsDeleted: true,
          Kpp: legalEntity?.Kpp || null,
          TaxPayerId: legalEntity?.TaxPayerId || null,
          CountConstantsPayments: legalEntity?.CountConstantsPayments || 0,
        },
      }).unwrap();

      showNotification('Юридическое лицо удалено успешно', SUCCESS);
      navigate(LEGAL_ENTITIES_PATH);
    } catch (error) {
      const message = error instanceof Error ? error.message : '';
      const cause = error instanceof Error ? (error.cause as string) : '';

      if (cause === ERROR) {
        showNotification(message, cause);
      } else if (cause === WARNING) {
        setServerError(message);
      }
    } finally {
      setIsLoading(false);
    }
  };

  return {
    isLoading,
    form,
    submitForm,
    serverError,
    setServerError,
    editableLegalEntitiesId,
    handleCancel,
    handelDeleteLegalEntity,
    hasUserRightForModuleView,
    hasUserRightToCreate,
    hasUserRightToEdit,
    isAddingOrEditingPage,
    setNewData,
    modalOpen,
    setModalOpen,
    setInputData,
  };
};
